/**
 * Admin V3 — Overview module — "Statistiques plateforme" card (Row 3)
 *
 * Wide card showing platform-wide totals: users, enterprises, and driver
 * breakdown (total / approved / online).
 *
 * Data sources:
 *   - GET /admin/platform-overview  → total_users, total_enterprises
 *   - GET /stats/dashboard          → drivers.{total, active, online}
 *
 * Explicit error state per owner directive (Phase 2.1 decision #5).
 * "Chauffeurs approuvés" label per Phase 2.1 decision #4.
 */

import React from 'react';
import { LayoutDashboard, AlertCircle, Clock, Users, Building2, Car } from 'lucide-react';
import { SectionCard, EmptyState } from '../../shared/visualTokens';
import { t } from '../../i18n';

const StatBlock = ({ label, value, icon: Icon, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/40 px-4 py-3 min-w-[140px]">
    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-base font-bold text-slate-900">{value ?? '—'}</div>
    </div>
  </div>
);

const OverviewPlatformCard = ({ platform, dashboard, locale = 'fr' }) => {
  // Loading state — wait for at least one of the two sources
  if (platform.loading && dashboard.loading) {
    return (
      <SectionCard
        title={t('overview.platform.title', locale)}
        icon={LayoutDashboard}
        iconClass="bg-slate-100 text-slate-600"
      >
        <EmptyState icon={Clock} title={t('overview.empty', locale)} />
      </SectionCard>
    );
  }

  // Error state — both sources failed
  if ((platform.error || !platform.data) && (dashboard.error || !dashboard.data)) {
    return (
      <SectionCard
        title={t('overview.platform.title', locale)}
        icon={LayoutDashboard}
        iconClass="bg-slate-100 text-slate-600"
      >
        <EmptyState
          icon={AlertCircle}
          title={t('overview.error', locale)}
          desc={platform.error || dashboard.error || undefined}
        />
      </SectionCard>
    );
  }

  const totalUsers = platform.data?.total_users;
  const totalEnterprises = platform.data?.total_enterprises;
  const totalDrivers = dashboard.data?.drivers?.total ?? platform.data?.total_drivers;
  const activeDrivers = dashboard.data?.drivers?.active;
  const onlineDrivers = dashboard.data?.drivers?.online;

  return (
    <SectionCard
      title={t('overview.platform.title', locale)}
      icon={LayoutDashboard}
      iconClass="bg-slate-100 text-slate-600"
    >
      <div className="flex flex-wrap gap-3">
        <StatBlock
          label={t('overview.platform.totalUsers', locale)}
          value={totalUsers}
          icon={Users}
          tone="bg-violet-100 text-violet-600"
        />
        <StatBlock
          label={t('overview.platform.totalEnterprises', locale)}
          value={totalEnterprises}
          icon={Building2}
          tone="bg-amber-100 text-amber-600"
        />
        <StatBlock
          label={t('overview.platform.totalDrivers', locale)}
          value={totalDrivers}
          icon={Car}
          tone="bg-slate-100 text-slate-600"
        />
        <StatBlock
          label={t('overview.platform.activeDrivers', locale)}
          value={activeDrivers}
          icon={Car}
          tone="bg-blue-100 text-blue-600"
        />
        <StatBlock
          label={t('overview.platform.onlineDrivers', locale)}
          value={onlineDrivers}
          icon={Car}
          tone="bg-emerald-100 text-emerald-600"
        />
      </div>
    </SectionCard>
  );
};

export default OverviewPlatformCard;
